import { useQueryClient, useQueries, queryOptions } from '@tanstack/react-query';
import { fetchEvOfferingById, fetchEvOfferingDtoById } from '@services/productOfferingService';
import type { EvBundleRelationship, Plan } from '@t/productOffering.types';
import { mapEvOfferingsToPlan } from '@utils/productOfferingMapper';

export type EvRelationshipOfferings = {
  relationship: EvBundleRelationship;
  offerings: Plan[];
  isLoading: boolean;
  /** 0 = no minimum required */
  minSelect: number;
  /** 0 = no maximum (unlimited); 1 = single-select */
  maxSelect: number;
};

type ResolvedGroup = {
  offerings: Plan[];
  minSelect: number;
  maxSelect: number;
};

const evOfferingDtoQueryOptions = (sessionId: string, offeringId: string, href?: string) =>
  queryOptions({
    queryKey: ['evOfferingDto', sessionId, offeringId] as const,
    queryFn: () => fetchEvOfferingDtoById(sessionId, offeringId, href),
    staleTime: 0,
    gcTime: 0,
  });

const evOfferingQueryOptions = (sessionId: string, offeringId: string, href?: string) =>
  queryOptions({
    queryKey: ['evOffering', sessionId, offeringId] as const,
    queryFn: () => fetchEvOfferingById(sessionId, offeringId, href),
    staleTime: 0,
    gcTime: 0,
  });

/**
 * Resolves the selectable offerings and selection constraints for one bundle relationship.
 *
 * Priority order for child offerings:
 *   1. `bundledGroupProductOffering[*].bundledProductOffering` — TMF620 group bundles (carries min/max option)
 *   2. `bundledProductOffering` — flat bundle members
 *   3. `childItems` — inline child DTOs
 *   4. `productOfferingRelationship` — relationship refs
 *
 * Falls back to the offering itself (leaf) with no constraints.
 */
const fetchEvOfferingResolved = async (
  queryClient: ReturnType<typeof useQueryClient>,
  sessionId: string,
  rel: EvBundleRelationship
): Promise<ResolvedGroup> => {
  const dto = await queryClient.ensureQueryData(
    evOfferingDtoQueryOptions(sessionId, rel.id, rel.href || undefined)
  );

  // 1. bundledGroupProductOffering — flatten members from all groups, take constraints from first group
  const groups = dto.bundledGroupProductOffering ?? [];
  const groupMembers = groups.flatMap((g) => g.bundledProductOffering ?? []).filter((m) => !!m.id);
  if (groupMembers.length > 0) {
    const option = groups[0]?.bundledGroupProductOfferingOption;
    const minSelect = option?.numberRelOfferLowerLimit ?? 0;
    const maxSelect = option?.numberRelOfferUpperLimit ?? 0;
    const offerings = await Promise.all(
      groupMembers.map((m) =>
        queryClient.ensureQueryData(evOfferingQueryOptions(sessionId, m.id, m.href))
      )
    );
    return { offerings, minSelect, maxSelect };
  }

  // 2. Flat bundledProductOffering members
  const flatMembers = (dto.bundledProductOffering ?? []).filter((m) => !!m.id);
  if (flatMembers.length > 0) {
    const offerings = await Promise.all(
      flatMembers.map((m) =>
        queryClient.ensureQueryData(evOfferingQueryOptions(sessionId, m.id, m.href))
      )
    );
    return { offerings, minSelect: 0, maxSelect: 0 };
  }

  // 3. Inline child items
  if (dto.childItems && dto.childItems.length > 0) {
    return { offerings: mapEvOfferingsToPlan(dto.childItems), minSelect: 0, maxSelect: 0 };
  }

  // 4. Nested relationship refs
  const relRefs = (dto.productOfferingRelationship ?? []).filter((r) => !!r.id);
  if (relRefs.length > 0) {
    const offerings = await Promise.all(
      relRefs.map((r) =>
        queryClient.ensureQueryData(evOfferingQueryOptions(sessionId, r.id, r.href))
      )
    );
    return { offerings, minSelect: 0, maxSelect: 0 };
  }

  // Leaf — return as-is with no constraints
  return { offerings: [mapEvOfferingsToPlan([dto])[0]], minSelect: 0, maxSelect: 0 };
};

/**
 * Fetches offerings and selection constraints for each bundle relationship on an EV plan.
 * Returns one entry per relationship, preserving the tab structure.
 */
export const useEvBundleOfferings = (
  sessionId: string,
  relationships: EvBundleRelationship[]
): EvRelationshipOfferings[] => {
  const queryClient = useQueryClient();

  const queries = useQueries({
    queries: relationships.map((rel) => ({
      queryKey: ['evOfferingResolved', sessionId, rel.id] as const,
      queryFn: () => fetchEvOfferingResolved(queryClient, sessionId, rel),
      enabled: !!sessionId && !!rel.id,
      staleTime: 0,
      gcTime: 0,
      refetchOnWindowFocus: false,
    })),
  });

  return relationships.map((rel, i) => ({
    relationship: rel,
    offerings: queries[i]?.data?.offerings ?? [],
    isLoading: queries[i]?.isLoading ?? false,
    minSelect: queries[i]?.data?.minSelect ?? 0,
    maxSelect: queries[i]?.data?.maxSelect ?? 0,
  }));
};
